import {
  replaceHistory,
  replaceHistoryRemoveParams,
} from "@tm/insurance/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CountrySelector,
  DateRangeSelector,
  TravellerSelector,
  type Country,
} from "../../../CommonLandingMweb/shared/MwebFormComponents";
import {
  SelectorTrackingSource,
  trackSelectorEvent,
} from "../../../CommonLandingMweb/shared/MwebSelectorTracking";
import LandingButton from "../../../LandingButton/LandingButton";
import type { ListingPageV2Params } from "../../ListingPageV2.types";
import {
  addDaysToDate,
  dateToUnixSeconds,
  formatDateFromTimestamp,
  unixSecondsToDate,
} from "../../utils/dateFormat";
import {
  FilterSortBar,
  type FilterSortCallbacks,
  type FilterSortChip,
} from "../FilterSort";
import {
  CollapsedRow,
  CountryLine,
  DetailLine,
  EditCol,
  EditLabel,
  Emphasis,
  FieldBlock,
  IconBtn,
  InfoBlock,
  LISTING_CONTEXT_PICKER_Z_INDEX,
  ModalBackdrop,
  ModalHeader,
  ModalPanel,
  ModalRoot,
  ModalTitle,
  StickyWrap,
} from "./Styles";

type CountryListEntry = {
  code?: string;
  name?: string;
  type?: string;
};

const EMPTY_CODES: string[] = [];
const EMPTY_COUNTRY_LIST: CountryListEntry[] = [];

interface ContextEditProps {
  heading?: string;
  travelDetails?: {
    countries?: {
      displayData?: string;
      countryCodes?: string[];
      /** Shown as destination field label when `countryList.label` is absent. */
      title?: unknown;
      /** When false, destination is read-only in the expanded sheet. */
      isEditable?: boolean;
      countryList?: { label?: unknown; value?: CountryListEntry[] };
    };
    dates?: {
      startDate?: number;
      endDate?: number;
      config?: { durationAllowed?: number };
      /** When false, dates are read-only in the expanded sheet. */
      isEditable?: boolean;
    };
    travellerCount?: { title?: string; value?: number };
  };
  filtersAndSorts?: FilterSortChip[];
  cta?: { expandedView?: string; collapsedView?: string };
  onBackPress?: () => void;
  /** Fired on the client right before URL is updated and the page reloads (e.g. show a full-page loader). */
  onConfirmNavigation?: () => void;
  onSortApply?: (sortKey: string, quoteIds: string[]) => void;
  onPriceUpdate?: (isNetPricing: boolean) => void;
  onPlanSelect?: (planIds: string[]) => void;
  tripOption?: string;
  planType?: string;
  isEditExpanded?: boolean;
  isAnnualMultitrip?: boolean;
  scrollElevated?: boolean;
  onCollapsedRowPress?: () => void;
  /**
   * `listing`: sync query params and reload (default).
   * `traveller`: reload with `isTravUpdated=true` only when traveller count changes; otherwise close the sheet without reload.
   */
  confirmNavigationMode?: "listing" | "traveller";
  /** Which page's analytics to fire for context edits. Defaults to listing. */
  trackingSource?: SelectorTrackingSource;
}

function BackArrowSvg() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
        fill="currentColor"
      />
    </svg>
  );
}

function EditPencilSvg() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
        fill="#008cff"
      />
    </svg>
  );
}

export const ContextEdit = ({
  heading,
  travelDetails,
  filtersAndSorts,
  cta,
  onBackPress,
  onConfirmNavigation,
  onSortApply,
  onPriceUpdate,
  onPlanSelect,
  tripOption,
  planType,
  isEditExpanded,
  isAnnualMultitrip = false,
  scrollElevated = false,
  onCollapsedRowPress,
  confirmNavigationMode = "listing",
  trackingSource = "listing",
}: ContextEditProps) => {
  const filterCallbacks = useMemo<FilterSortCallbacks>(
    () => ({
      onSortApply: (sortKey, quoteIds) => onSortApply?.(sortKey, quoteIds),
      onPriceUpdate: (isNet) => onPriceUpdate?.(isNet),
      onPlanSelect: (ids) => onPlanSelect?.(ids),
    }),
    [onSortApply, onPriceUpdate, onPlanSelect]
  );
  const countryCodesRaw = travelDetails?.countries?.countryCodes;
  const countryCodes = countryCodesRaw ?? EMPTY_CODES;
  const listRaw = travelDetails?.countries?.countryList?.value;
  const list = listRaw ?? EMPTY_COUNTRY_LIST;

  const countriesDisabled = travelDetails?.countries?.isEditable === false;
  const datesDisabled = travelDetails?.dates?.isEditable === false;

  const preSelected = useMemo(() => {
    return list.filter(
      (c) => c.code && countryCodes.includes(c.code)
    ) as CountryListEntry[];
  }, [list, countryCodes]);

  const travelCountryCodesKey = useMemo(
    () => (countryCodesRaw ?? EMPTY_CODES).slice().sort().join(","),
    [countryCodesRaw]
  );
  const listCodesKey = useMemo(
    () =>
      list
        .map((c) => c.code)
        .filter(Boolean)
        .sort()
        .join(","),
    [list]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [countryError, setCountryError] = useState("");
  const initialTravellerCountForConfirmRef = useRef(1);
  const [selectedCodes, setSelectedCodes] = useState<string[]>(() =>
    preSelected.map((c) => c.code!).filter(Boolean)
  );

  const startTs = travelDetails?.dates?.startDate;
  const endTs = travelDetails?.dates?.endDate;
  const [startDate, setStartDate] = useState(
    () => unixSecondsToDate(startTs) || new Date()
  );
  const [endDate, setEndDate] = useState(
    () => unixSecondsToDate(endTs) || new Date()
  );
  const [travellerCount, setTravellerCount] = useState(
    travelDetails?.travellerCount?.value ?? 1
  );

  /** Sync draft from API / URL only when server-provided codes or list contents change — not on every render. */
  useEffect(() => {
    const codes = countryCodesRaw;
    if (!codes?.length && !list.length) return;
    if (!codes?.length) {
      setSelectedCodes([]);
      return;
    }
    if (!list.length && codes.length) {
      setSelectedCodes([...codes]);
      return;
    }
    setSelectedCodes(
      list
        .filter((c) => c.code && codes.includes(c.code))
        .map((c) => c.code!)
        .filter(Boolean)
    );
  }, [travelCountryCodesKey, listCodesKey]); // eslint-disable-line react-hooks/exhaustive-deps -- list/countryCodesRaw omitted: unstable refs would reset draft after each edit

  useEffect(() => {
    const s = unixSecondsToDate(startTs);
    const e = unixSecondsToDate(endTs);
    if (s) setStartDate(s);
    if (e) setEndDate(e);
  }, [startTs, endTs]);

  useEffect(() => {
    if (travelDetails?.travellerCount?.value != null) {
      setTravellerCount(travelDetails.travellerCount.value);
    }
  }, [travelDetails?.travellerCount?.value]);

  const resetDraftToTravelDetails = useCallback(() => {
    setSelectedCodes(preSelected.map((c) => c.code!).filter(Boolean));
    const s = unixSecondsToDate(startTs);
    const e = unixSecondsToDate(endTs);
    if (s) setStartDate(s);
    if (e) setEndDate(e);
    if (travelDetails?.travellerCount?.value != null) {
      setTravellerCount(travelDetails.travellerCount.value);
    } else {
      setTravellerCount(1);
    }
    setCountryError("");
  }, [preSelected, startTs, endTs, travelDetails?.travellerCount?.value]);

  const beginModalSession = useCallback(() => {
    resetDraftToTravelDetails();
    initialTravellerCountForConfirmRef.current =
      travelDetails?.travellerCount?.value ?? 1;
    setModalOpen(true);
  }, [resetDraftToTravelDetails, travelDetails?.travellerCount?.value]);

  const dismissModalWithoutConfirm = () => {
    resetDraftToTravelDetails();
    setModalOpen(false);
  };

  useEffect(() => {
    if (!isEditExpanded || onCollapsedRowPress) return;
    const t = window.setTimeout(() => {
      beginModalSession();
    }, 300);
    return () => clearTimeout(t);
  }, [isEditExpanded, onCollapsedRowPress, beginModalSession]);

  const countriesLabel = travelDetails?.countries?.displayData || "Destination";

  const countriesDisplayLabel = useMemo(() => {
    if (!selectedCodes.length) return countriesLabel;
    const codeSet = new Set(selectedCodes);
    const names = list
      .filter((c) => c.code && codeSet.has(c.code))
      .map((c) => c.name || c.code)
      .filter(Boolean) as string[];
    return names.length ? names.join(", ") : countriesLabel;
  }, [list, selectedCodes, countriesLabel]);

  const startLabel = formatDateFromTimestamp(startTs);
  const endLabel = formatDateFromTimestamp(endTs);
  const travellerText = travellerCount === 1 ? "Traveller" : "Travellers";

  const selectedCountries = useMemo<Country[]>(() => {
    return list
      .filter((c) => c.code && selectedCodes.includes(c.code))
      .map((c) => ({ code: c.code!, name: c.name || c.code! }));
  }, [list, selectedCodes]);

  const countryListForSelector = useMemo(() => {
    const label =
      travelDetails?.countries?.countryList?.label ??
      travelDetails?.countries?.title ??
      "Travel Destination";
    const countries: Country[] = list
      .filter((c) => c.type === "COUNTRY" || c.type === "REGION")
      .map((c) => ({ code: c.code || "", name: c.name || "" }))
      .filter((c) => c.code);
    return { label, value: countries };
  }, [list, travelDetails?.countries?.countryList?.label]);

  const handleCountryChange = useCallback((countries: Country[]) => {
    setCountryError("");
    setSelectedCodes(
      countries
        .map((c) => c.code)
        .filter((code): code is string => Boolean(code))
    );
  }, []);

  const toggleCountry = (entry: CountryListEntry) => {
    if (!entry.code || entry.type === "HEADER") return;
    setCountryError("");
    const code = entry.code;
    const exclusive = Boolean(
      (entry as { isExclusiveSelection?: boolean }).isExclusiveSelection
    );
    setSelectedCodes((prev) => {
      if (exclusive) {
        return prev.includes(code) ? [] : [code];
      }
      if (prev.includes(code)) {
        return prev.filter((c) => c !== code);
      }
      return [...prev, code];
    });
  };

  const onConfirm = () => {
    const countriesParam =
      countriesDisabled && countryCodesRaw?.length
        ? countryCodesRaw.join(",")
        : selectedCodes.join(",");
    const hasCountrySelection =
      selectedCodes.length > 0 || Boolean(countryCodesRaw?.length);
    if (!hasCountrySelection) {
      setCountryError("Please select your destination country");
      return;
    }
    let end = endDate;
    if (isAnnualMultitrip) {
      const dur = travelDetails?.dates?.config?.durationAllowed;
      end = addDaysToDate(startDate, dur);
      setEndDate(end);
    }
    const nextParams: ListingPageV2Params = {
      countries: countriesParam,
      startDate: String(dateToUnixSeconds(startDate)),
      endDate: String(dateToUnixSeconds(end)),
      travellers: String(travellerCount),
      planType,
      tripOption,
    };

    if (typeof window === "undefined") {
      setModalOpen(false);
      return;
    }

    if (confirmNavigationMode === "traveller") {
      const baseline = initialTravellerCountForConfirmRef.current;
      if (travellerCount === baseline) {
        replaceHistoryRemoveParams("isTravUpdated");
        setModalOpen(false);
        return;
      }
    }

    trackSelectorEvent(trackingSource, "modify_search_clicked", {
      type: planType,
    });

    onConfirmNavigation?.();

    replaceHistory("countries", nextParams.countries || "");
    replaceHistory("startDate", nextParams.startDate || "");
    replaceHistory("endDate", nextParams.endDate || "");
    replaceHistory("travellers", nextParams.travellers || "");

    if (planType) {
      replaceHistory("planType", planType);
    } else {
      replaceHistoryRemoveParams("planType");
    }
    if (tripOption) {
      replaceHistory("tripOption", tripOption);
    } else {
      replaceHistoryRemoveParams("tripOption");
    }

    setModalOpen(false);
    window.location.reload();
  };

  const listRows = useMemo(() => {
    return list.map((entry, idx) => ({ entry, idx }));
  }, [list]);

  return (
    <>
      <StickyWrap
        $elevated={scrollElevated}
        $hasFilters={Boolean(filtersAndSorts?.length)}
      >
        <CollapsedRow
          type="button"
          onClick={() => {
            trackSelectorEvent(trackingSource, "context_edit_clicked");
            onCollapsedRowPress ? onCollapsedRowPress() : beginModalSession();
          }}
          aria-label="Edit travel details"
        >
          <IconBtn
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onBackPress?.();
            }}
            aria-label="Back"
          >
            <BackArrowSvg />
          </IconBtn>
          <InfoBlock>
            <CountryLine>{countriesDisplayLabel}</CountryLine>
            <DetailLine>
              <Emphasis>{startLabel}</Emphasis>
              {" - "}
              <Emphasis>{endLabel}</Emphasis>
              {", "}
              <Emphasis>{travellerCount}</Emphasis> {travellerText}
            </DetailLine>
          </InfoBlock>
          <EditCol>
            <EditPencilSvg />
            <EditLabel>{cta?.collapsedView || "Edit"}</EditLabel>
          </EditCol>
        </CollapsedRow>
        {filtersAndSorts?.length ? (
          <FilterSortBar chips={filtersAndSorts} callbacks={filterCallbacks} />
        ) : null}
      </StickyWrap>

      {modalOpen && !onCollapsedRowPress ? (
        <ModalRoot role="dialog" aria-modal aria-labelledby="ctx-edit-title">
          <ModalBackdrop
            type="button"
            aria-label="Close"
            onClick={dismissModalWithoutConfirm}
          />
          <ModalPanel>
            <ModalHeader>
              <IconBtn
                type="button"
                onClick={dismissModalWithoutConfirm}
                aria-label="Close"
              >
                <BackArrowSvg />
              </IconBtn>
              <ModalTitle id="ctx-edit-title">
                {heading || "International Travel + Medical Insurance"}
              </ModalTitle>
            </ModalHeader>

            <FieldBlock>
              <CountrySelector
                countryList={countryListForSelector}
                initialSelectedCountries={selectedCountries}
                onCountryChange={handleCountryChange}
                errorText={countryError || undefined}
                zIndex={LISTING_CONTEXT_PICKER_Z_INDEX}
                disabled={countriesDisabled}
                readOnlyDisplayText={
                  countriesDisabled ? countriesDisplayLabel : undefined
                }
                trackingSource={trackingSource}
              />
            </FieldBlock>

            <FieldBlock>
              <DateRangeSelector
                startDate={startDate}
                endDate={endDate}
                onDateChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                isAnnualMultitrip={isAnnualMultitrip}
                zIndex={LISTING_CONTEXT_PICKER_Z_INDEX}
                disabled={datesDisabled}
                trackingSource={trackingSource}
              />
            </FieldBlock>

            <FieldBlock>
              <TravellerSelector
                travellerInfo={{
                  label: travelDetails?.travellerCount?.title || "Travellers",
                }}
                travellerCount={travellerCount}
                onTravellerCountChange={setTravellerCount}
                trackingSource={trackingSource}
              />
            </FieldBlock>

            {cta?.expandedView ? (
              <LandingButton
                customTitle={cta.expandedView}
                customWidth="100%"
                customHeight="44px"
                margin="8px 0 0"
                shadowVariant="none"
                onClick={onConfirm}
              />
            ) : null}
          </ModalPanel>
        </ModalRoot>
      ) : null}
    </>
  );
};
